import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {forkJoin, Observable} from 'rxjs';

import {ElasticSearchRequest} from '@models/elastic-search/request/elastic-search-request';
import {ElasticSearchFilterValue} from '@models/elastic-search/request/elastic-search-filter-value';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ELASTIC_SEARCH_SORT_ORDER} from '@models/elastic-search/request/elastic-search-sort-order.enum';
import {ELASTIC_SEARCH_CONSTANTS} from '@constants/elastic-search/elastic-search-constants.constant';
import {ELASTIC_SEARCH_RETURNED_FIELDS} from '@constants/elastic-search/elastic-search-returned-fields.constant';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {Role} from '@models/user/role';

@Injectable({
  providedIn: 'root'
})
export class LeftMenuSearchServiceService {

  private ELASTIC_API_PREFIX = '/api/elastic/';
  private BASE_MENU_ITEM_SUFFIX = this.ELASTIC_API_PREFIX + 'baseMenuItem/';
  private USER_MENU_ITEM_SUFFIX = this.ELASTIC_API_PREFIX + 'userMenuItem/';
  private TEMPLATE_MENU_ITEM_SUFFIX = this.ELASTIC_API_PREFIX + 'templateMenuItem/';
  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public validateSearchTerm(searchTerm: string): boolean {
    if (!searchTerm || searchTerm.isNullOrWhitespace()) {
      return false;
    }

    if (searchTerm.startsWith('"') && searchTerm.endsWith('"')) {
      return true;
    } else {
      if (searchTerm.length < ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_TERM_MIN_SIZE) {
        return false;
      }
    }
    return true;

  }

  public searchMenuItems(searchedValues: string[], firebaseUserId: string, userGroupId: number, role: Role, from: number, whiteListedMachineryGroups: number[] | null, availableMachineIds: number[] | null): Observable<any> {
    if (role.isSuperAdminRole) {
      return this.searchMenu(searchedValues, firebaseUserId, null, from, role, null, null);
    }
    return this.searchMenu(searchedValues, firebaseUserId, userGroupId, from, role, whiteListedMachineryGroups, availableMachineIds);

  }

  private generateSearchText(searchedValues: string[]): string {
    if (!searchedValues || searchedValues.length === 0) {
      return '';
    }

    const regexPattern = new RegExp(ELASTIC_SEARCH_CONSTANTS.SPECIAL_CHARACTERS_PATTERN.join('|'), 'g');
    const sanitizedSearchValues: string[] = [];

    for (const searchedValue of searchedValues) {
      if (!searchedValue || searchedValue.trim() === '') {
        continue;
      }

      const keywords = searchedValue.trim().split(regexPattern);
      const isQuotedValue = searchedValue.startsWith('"') && searchedValue.endsWith('"');

      if (isQuotedValue && keywords.length > 1) {
        const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim()).filter(Boolean);
        sanitizedSearchValues.push(sanitizedKeywords.join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `));
      } else {
        if (searchedValue.length >= ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_TERM_MIN_SIZE) {
          const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim() !== '' ? `*${keyword.trim()}*` : '');
          sanitizedSearchValues.push(sanitizedKeywords.filter(Boolean).join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `));
        }
      }
    }

    return sanitizedSearchValues.join(ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN);
  }

  private searchMenu(searchedValues: string[], firebaseUserId: string, parentId: number | null, from: number, role: Role, whiteListedMachineryGroups: number[] | null, availableMachineIds: number[] | null): Observable<any> {
    const baseMenuSubscription = this.searchBaseMenuItem(searchedValues, parentId, from, role, whiteListedMachineryGroups);
    const usersMenuSubscription = this.searchUserMenuItem(searchedValues, firebaseUserId, from, role);
    const templateMenuSubscription = this.searchTemplateMenuItem(searchedValues, from, availableMachineIds);
    return forkJoin([baseMenuSubscription, usersMenuSubscription, templateMenuSubscription]);
  }

  private searchBaseMenuItem(searchedValues: string[], parentId: number | null, from: number, role: Role, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const baseMenuRequest: ElasticSearchRequest = this.createBaseMenuSearchRequest(searchedValues, parentId, from, role, whiteListedMachineryGroups);
    const url: string = this.BASE_URL + this.BASE_MENU_ITEM_SUFFIX;
    return this.http.post<BaseMenuItem>(url, baseMenuRequest, HTTPS_REQUEST_OPTIONS);
  }

  private searchUserMenuItem(searchedValues: string[], firebaseUserId: string, from: number, role: Role): Observable<any> {
    const userMenuSearchRequest: ElasticSearchRequest = this.createUserMenuSearchRequest(searchedValues, firebaseUserId, from, role);
    const url: string = this.BASE_URL + this.USER_MENU_ITEM_SUFFIX;
    return this.http.post<BaseMenuItem>(url, userMenuSearchRequest, HTTPS_REQUEST_OPTIONS);
  }

  private searchTemplateMenuItem(searchedValues: string[], from: number, availableMachineIds: number[] | null) {
    const templateMenuRequest: ElasticSearchRequest = this.createTemplateMenuSearchRequest(searchedValues, from, availableMachineIds);
    const url: string = this.BASE_URL + this.TEMPLATE_MENU_ITEM_SUFFIX;
    return this.http.post<BaseMenuItem>(url, templateMenuRequest, HTTPS_REQUEST_OPTIONS);
  }

  private createUserMenuSearchRequest(searchedValues: string[], firebaseUserId: string, from: number, role: Role): ElasticSearchRequest {
    const searchRequest: ElasticSearchRequest = this.createDefaultMenuSearchRequest(searchedValues, from, role);
    searchRequest.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.USER_MENU_RETURNED_FIELDS;
    searchRequest.filter = [new ElasticSearchFilterValue('firebaseUserId', firebaseUserId, true)];
    return searchRequest;
  }


  private createBaseMenuSearchRequest(searchedValues: string[], parentId: number | null, from: number, role: Role, whiteListedMachineryGroups: number[] | null): ElasticSearchRequest {
    const searchRequest: ElasticSearchRequest = this.createDefaultMenuSearchRequest(searchedValues, from, role);
    searchRequest.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.BASE_MENU_RETURNED_FIELDS;
    if (parentId) {
      searchRequest.filter = [new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINERY_GROUP_ANCESTORS_PROPERTY_NAME, String(parentId))];
    }
    if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
      searchRequest.whiteListFilter = [new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINERY_GROUP_ANCESTORS_PROPERTY_NAME, String(whiteListedMachineryGroups.join(',')))];
      searchRequest.whiteListFilter.push(new ElasticSearchFilterValue('machineGroupId', String(whiteListedMachineryGroups.join(','))));
      searchRequest.whiteListFilter.push(new ElasticSearchFilterValue('parentId', String(whiteListedMachineryGroups.join(','))));

    }
    return searchRequest;
  }

  private createTemplateMenuSearchRequest(searchedValues: string[], from: number, availableMachineIds: number[] | null): ElasticSearchRequest {
    const searchRequest: ElasticSearchRequest = new ElasticSearchRequest();
    searchRequest.searchText = this.generateSearchText(searchedValues);
    searchRequest.fieldsToSearch = [ELASTIC_SEARCH_CONSTANTS.TEMPLATE_MENU_ITEM_NAME_FIELD];
    searchRequest.from = from;
    searchRequest.size = ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_RESULTS_SIZE;
    searchRequest.sort = new ElasticSearchSortValue(ELASTIC_SEARCH_CONSTANTS.TEMPLATE_MENU_ITEM_NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);

    searchRequest.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.TEMPLATE_MENU_RETURNED_FIELDS;

    if (availableMachineIds) {
      searchRequest.whiteListFilter = [new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.TEMPLATE_MENU_ITEM_REFERENCE_MACHINE_ID_FIELD, String(availableMachineIds.join(',')))];
    }

    return searchRequest;
  }

  private createDefaultMenuSearchRequest(searchedValues: string[], from: number, role: Role): ElasticSearchRequest {
    const searchRequest: ElasticSearchRequest = new ElasticSearchRequest();
    searchRequest.searchText = this.generateSearchText(searchedValues);
    searchRequest.fieldsToSearch = [ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_NAME_FIELD];
    searchRequest.from = from;
    searchRequest.size = ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_RESULTS_SIZE;
    searchRequest.sort = new ElasticSearchSortValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    searchRequest.excludedFilter = this.createExclusionFilters(role);
    return searchRequest;
  }

  private createExclusionFilters(role: Role): ElasticSearchFilterValue[] {
    const exclusionFilters: ElasticSearchFilterValue[] = [];
    if (role.isUserRole) {
      exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_TYPE_FIELD, MenuItemType.TABLEAU_PAGE, true));
    }
    if (role.isDistributorRole || role.isViewerRole) {
      exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_TYPE_FIELD, MenuItemType.TABLEAU_PAGE, true));
      exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_TYPE_FIELD, MenuItemType.USER_DEFINED_PAGE, true));
      exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_TYPE_FIELD, MenuItemType.NODE, true));
    }
    if (role.isViewerRole) {
      exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.PAGE_UNDER_MACHINERY_GROUP, 'true'));
    }

    exclusionFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MENU_ITEM_TYPE_FIELD, MenuItemType.MACHINE_GROUP_STATUS_PAGE, true));
    return exclusionFilters;
  }


}
